import {Component, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Employee, ReduxPagination} from '@kion/data-models';
import {EmployeesFacade} from '@kion/data-access/data-access-employees';
import {UsersFacade} from '@kion/data-access/data-access-users';
import {CorrelationObserverService} from '@kion/ngrx-helper';
import {RolesFacade} from '@kion/data-access/data-access-roles';
import {DropDownOption, ToastService} from '@kion/ui';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {TranslateService} from '@ngx-translate/core';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {RoleMapDialogComponent} from '../../../../../fs-ui/src/lib/dialogs/role-map-dialog/role-map-dialog.component';
import {take} from "rxjs/operators";
import {SubSink} from "subsink";

@Component({
    selector: 'kion-cms-nw-user-list',
    templateUrl: './nwp-user-list.component.html',
    styleUrls: ['./nwp-user-list.component.scss']
})
export class NwpUserListComponent implements OnInit, OnDestroy {

    employees$: Observable<Employee[]>;
    userId: string;
    pagination$: Observable<ReduxPagination>;
    query$: Observable<string>;
    resetValue = false;
    loading = false;
    statusFilterSelected = null;
    searchTerm = null;
    statusFilterValues$: BehaviorSubject<DropDownOption[] | null>;
    statusFilterDefaults = [{
        key: '',
        value: 'all',
        selected: true,
    } as DropDownOption,
        {
            key: 'pending',
            value: 'pending',
            selected: false,
        } as DropDownOption,
        {
            key: 'accepted',
            value: 'accepted',
            selected: false,
        } as DropDownOption,
        {
            key: 'resent',
            value: 'resent',
            selected: false,
        } as DropDownOption];

    private readonly subSink = new SubSink();

    constructor(
        private employeesFacade: EmployeesFacade,
        private usersFacade: UsersFacade,
        private correlationObserver: CorrelationObserverService,
        private rolesFacade: RolesFacade,
        private toastMessageService: ToastService,
        private dialog: NgbModal,
        private translationService: TranslateService,
        private observer: CorrelationObserverService,
    ) {
        this.employees$ = this.employeesFacade.searchResults$;
        this.statusFilterValues$ = new BehaviorSubject(null);
    }

    ngOnInit(): void {
        this.loading = true;
        this.employeesFacade.search();
        this.pagination$ = this.employeesFacade.pagination$;
        this.query$ = this.employeesFacade.query$;
        this.employees$.pipe(
        ).subscribe(() => this.loading = false);

        setTimeout(() => {
            this.statusFilterDefaults.forEach((option) => {
                option.value = this.translationService.instant('admin.invite.status.' + option.value);
            });
            this.statusFilterValues$.next(this.statusFilterDefaults);
        }, 1000);
    }

    resendInvite(employee: Employee): void {
        this.subSink.sink = this.correlationObserver.subscribeToAction(
            this.usersFacade.resendInvitation(employee.invitationId),
            () => {
                this.employeesFacade.loadAll();
                this.toastMessageService.show(
                    {
                        text: this.translationService.instant('admin.toast.invitation.resend.success'),
                        type: 'success',
                    }
                )
            },
            () => {
                this.toastMessageService.show(
                    {
                        text: this.translationService.instant('admin.toast.invitation.resend.error'),
                        type: 'error',
                    }
                )
            }
        )
    }

    deleteInvite(employee: Employee): void {
        this.subSink.sink = this.correlationObserver.subscribeToAction(
            this.usersFacade.deleteInvitation(employee.invitationId),
            () => {
                this.employeesFacade.search();
                this.toastMessageService.show(
                    {
                        text: this.translationService.instant('admin.toast.invitation.delete.success'),
                        type: 'success',
                    }
                )
            },
            () => {
                this.toastMessageService.show(
                    {
                        text: this.translationService.instant('admin.toast.invitation.delete.error'),
                        type: 'error',
                    }
                )
            }
        )
    }

    openDialog(userId: string): void {
        const dialogRef = this.dialog.open(RoleMapDialogComponent);
        dialogRef.componentInstance.data = {
            userId: userId,
            baseSite: ''
        }
    }

    perPageChanged(perPage: number): void {
        this.loading = true;
        this.subSink.sink = this.observer.subscribeToAction(
            this.employeesFacade.updatePerPage(perPage),
            () =>
                this.query$
                    .pipe(take(1))
                    .subscribe((query) => this.employeesFacade.search(query, true))
        );
    }

    pageChanged(page: number): void {
        this.loading = true;
        this.subSink.sink = this.observer.subscribeToAction(this.employeesFacade.updatePage(page), () =>
            this.query$
                .pipe(take(1))
                .subscribe((query) => this.employeesFacade.search(query, true))
        );
    }

    ngOnDestroy() {
        this.subSink.unsubscribe();
    }

    search(searchTerm: string) {
        this.loading = true;
        this.searchTerm = searchTerm;
        const status = this.statusFilterSelected ? 'status:' + this.statusFilterSelected : '';
        const query = searchTerm?.length >= 2 ? status !== '' ? searchTerm + ':' + status : searchTerm : status !== '' ? status : null;

        if (query) {
            this.employeesFacade.search(query, false);
            this.employeesFacade.updateQuery(query);
        } else {
            this.employeesFacade.search(null, false, true);
        }
    }


    searchBoxResetValue() {
        this.resetValue = false;
    }

    searchBoxCleared() {
        this.loading = true;
        this.employeesFacade.search(null, false, true);

    }
    statusFilterChanged(value: DropDownOption) {
        this.statusFilterValues$.next( this.statusFilterDefaults
            .map((option) =>
                option.key === value.key ? {...option, selected: true} : {...option, selected: false}));
        this.statusFilterSelected = value.key;
        this.search(this.searchTerm);
    }
}
